<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { api, type ProspectorRunRow, type ShortlistChangeRow, type ShortlistCollectorRun, type ShortlistFund } from '../../../api/client'
import ProspectMeasures from './ProspectMeasures.vue'
import { fmt } from './shortlistFormat'

const props = defineProps<{ evicted: ShortlistFund[]; rejected: ShortlistFund[]; proposed: ShortlistFund[]; reloadToken?: number }>()

const changes = ref<ShortlistChangeRow[]>([])
const changesError = ref<string | null>(null)
const changesLoading = ref(true)
const limit = ref(200)
const filter = ref('')
const runs = ref<ShortlistCollectorRun[]>([])
const runsError = ref<string | null>(null)
const runsLoading = ref(true)

const pruns = ref<ProspectorRunRow[]>([])
const prunsError = ref<string | null>(null)
const prunsLoading = ref(true)

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e))

async function loadChanges(n: number) {
  changesLoading.value = true
  changesError.value = null
  try { changes.value = await api.fundShortlistHistory(n); limit.value = n } catch (e) { changesError.value = msg(e) }
  changesLoading.value = false
}
async function loadRuns() {
  try { runs.value = await api.fundShortlistCollectorRuns(30) } catch (e) { runsError.value = msg(e) }
  runsLoading.value = false
}
async function loadPruns() {
  try { pruns.value = await api.fundShortlistProspectorRuns(20); prunsError.value = null } catch (e) { prunsError.value = msg(e) }
  prunsLoading.value = false
}
onMounted(() => { void loadChanges(200); void loadRuns(); void loadPruns() })
watch(() => props.reloadToken, () => { void loadChanges(limit.value); void loadRuns(); void loadPruns() })

const openFunds = ref<Set<string>>(new Set())
function toggleFund(id: string) {
  const n = new Set(openFunds.value)
  if (n.has(id)) n.delete(id); else n.add(id)
  openFunds.value = n
}

const filtered = computed(() => {
  const q = filter.value.trim().toLowerCase()
  return q ? changes.value.filter(c => c.fundKey.toLowerCase().includes(q)) : changes.value
})

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
}
function detailFull(d: unknown): string {
  if (d === null || d === undefined) return ''
  try { return JSON.stringify(d) ?? '' } catch { return String(d) }
}
const detailShort = (d: unknown) => {
  const s = detailFull(d)
  return s.length > 120 ? s.slice(0, 120) + '…' : s
}
function failures(r: ShortlistCollectorRun): [string, string][] {
  const d = r.detail
  if (!d || typeof d !== 'object') return []
  const f = (d as Record<string, unknown>).failures
  if (!f || typeof f !== 'object' || Array.isArray(f)) return []
  return Object.entries(f as Record<string, unknown>).map(([k, v]) => [k, typeof v === 'string' ? v : detailFull(v)])
}
const runBad = (r: ShortlistCollectorRun) => r.status === 'failed' || r.status === 'partial' || r.fundsFailed > 0
interface PCand { fund_name?: string; fund_key?: string; sector?: string; outcome?: string; reason?: string }
function pdetail(r: ProspectorRunRow): { candidates: PCand[]; summary: string; stopped: string; reason: string; error: string } {
  const d = r.detail
  const empty = { candidates: [], summary: '', stopped: '', reason: '', error: '' }
  if (!d || typeof d !== 'object') return empty
  const o = d as Record<string, unknown>
  const str = (v: unknown) => (typeof v === 'string' ? v : '')
  const cands = Array.isArray(o.candidates) ? (o.candidates as unknown[]).filter((c): c is PCand => !!c && typeof c === 'object') : []
  return { candidates: cands, summary: str(o.summary), stopped: str(o.stopped_reason), reason: str(o.reason), error: str(o.error) }
}
const hasPdetail = (r: ProspectorRunRow) => { const p = pdetail(r); return p.candidates.length > 0 || !!(p.summary || p.stopped || p.reason || p.error) }
const money = (n: number | null | undefined) => (typeof n === 'number' ? `$${n.toFixed(2)}` : '—')
const nearBudget = (r: ProspectorRunRow) => typeof r.spendUsd === 'number' && typeof r.budgetUsd === 'number' && r.budgetUsd > 0 && r.spendUsd >= 0.9 * r.budgetUsd
const retired = computed(() => [...props.evicted, ...props.rejected])
</script>

<template>
  <div class="ph" data-testid="prospects-history">
    <details class="ph-sec" data-testid="prospects-changes">
      <summary>Change log</summary>
      <div class="ph-body">
        <div class="ph-tools">
          <input v-model="filter" type="text" placeholder="Filter by fund key" aria-label="Filter by fund key" data-testid="changes-filter" />
          <button v-if="limit < 1000" type="button" :disabled="changesLoading" data-testid="changes-more" @click="loadChanges(1000)">Load more</button>
        </div>
        <p v-if="changesError" class="err">Could not load the change log: {{ changesError }}</p>
        <p v-else-if="changesLoading && changes.length === 0" class="dim">Loading…</p>
        <p v-else-if="filtered.length === 0" class="dim">No changes.</p>
        <p v-if="!changesError && changes.length" class="dim" data-testid="changes-note">{{ limit >= 1000 ? 'Showing the latest 1000 changes. ' : '' }}The filter applies to the loaded rows only.</p>
        <div v-if="!changesError && filtered.length" class="scroll">
          <table>
            <thead><tr><th>At</th><th>Fund</th><th>Action</th><th>Reason</th><th>Detail</th></tr></thead>
            <tbody>
              <tr v-for="c in filtered" :key="c.changeId" data-testid="change-row">
                <td>{{ fmtDateTime(c.at) }}</td>
                <td>{{ c.fundKey }}</td>
                <td>{{ c.action }}</td>
                <td>{{ c.reason ?? '—' }}</td>
                <td class="mono" :title="detailFull(c.detail)">
                  <details v-if="detailFull(c.detail).length > 120" class="ph-nested">
                    <summary>{{ detailShort(c.detail) }}</summary>
                    <pre>{{ detailFull(c.detail) }}</pre>
                  </details>
                  <template v-else>{{ detailFull(c.detail) }}</template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </details>

    <details class="ph-sec" data-testid="prospects-collector-runs">
      <summary>Collector runs</summary>
      <div class="ph-body">
        <p v-if="runsError" class="err">Could not load collector runs: {{ runsError }}</p>
        <p v-else-if="runsLoading" class="dim">Loading…</p>
        <p v-else-if="runs.length === 0" class="dim">No runs.</p>
        <div v-else class="scroll">
          <table>
            <thead><tr><th>Started</th><th>Finished</th><th>Status</th><th>OK</th><th>Failed</th></tr></thead>
            <tbody>
              <template v-for="r in runs" :key="r.runId">
                <tr :class="{ bad: runBad(r) }" data-testid="run-row">
                  <td>{{ fmtDateTime(r.startedAt) }}</td>
                  <td>{{ fmtDateTime(r.finishedAt) }}</td>
                  <td>{{ r.status }}</td>
                  <td>{{ r.fundsOk }}</td>
                  <td>{{ r.fundsFailed }}</td>
                </tr>
                <tr v-if="failures(r).length" :class="{ bad: runBad(r) }">
                  <td colspan="5">
                    <details class="ph-nested">
                      <summary>Failures ({{ failures(r).length }})</summary>
                      <ul><li v-for="[k, why] in failures(r)" :key="k"><strong>{{ k }}</strong> {{ why }}</li></ul>
                    </details>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </details>

    <details class="ph-sec" data-testid="prospects-prospector-runs">
      <summary>Prospector runs</summary>
      <div class="ph-body">
        <p v-if="prunsError" class="err">Could not load prospector runs: {{ prunsError }}</p>
        <p v-else-if="prunsLoading" class="dim">Loading…</p>
        <p v-else-if="pruns.length === 0" class="dim">No prospector runs yet</p>
        <div v-else class="scroll">
          <table>
            <thead><tr><th>Started</th><th>Status</th><th>Spend</th><th>Searches</th><th>Proposed</th><th>Rejected</th><th>Duplicates</th></tr></thead>
            <tbody>
              <template v-for="r in pruns" :key="r.runId">
                <tr data-testid="prun-row">
                  <td>{{ fmtDateTime(r.startedAt) }}</td>
                  <td>{{ r.status }}</td>
                  <td :class="{ amber: nearBudget(r) }" data-testid="prun-spend">{{ money(r.spendUsd) }} / {{ money(r.budgetUsd) }}</td>
                  <td>{{ r.searches }}</td>
                  <td>{{ r.proposed }}</td>
                  <td>{{ r.rejected }}</td>
                  <td>{{ r.duplicates }}</td>
                </tr>
                <tr v-if="hasPdetail(r)">
                  <td colspan="7">
                    <details class="ph-nested" data-testid="prun-detail">
                      <summary>Details ({{ pdetail(r).candidates.length }} candidates)</summary>
                      <p v-if="pdetail(r).reason" data-testid="prun-reason">Reason: {{ pdetail(r).reason }}</p>
                      <p v-if="pdetail(r).error" class="err" data-testid="prun-error">Error: {{ pdetail(r).error }}</p>
                      <p v-if="pdetail(r).stopped" class="dim">Stopped: {{ pdetail(r).stopped }}</p>
                      <p v-if="pdetail(r).summary">{{ pdetail(r).summary }}</p>
                      <ul>
                        <li v-for="(c, i) in pdetail(r).candidates" :key="(c.fund_key ?? '') + i" data-testid="prun-candidate">
                          <strong>{{ c.fund_name ?? c.fund_key ?? '—' }}</strong> · {{ c.sector ?? '—' }} · {{ c.outcome ?? '—' }}<template v-if="c.reason"> · {{ c.reason }}</template>
                        </li>
                      </ul>
                    </details>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </details>

    <details class="ph-sec" data-testid="prospects-retired">
      <summary>Rejected &amp; evicted</summary>
      <div class="ph-body">
        <p v-if="retired.length === 0" class="dim">none</p>
        <ul v-else class="list">
          <li v-for="f in retired" :key="f.status + f.fundKey" :data-testid="`retired-${f.status}-${f.fundKey}`">
            <button type="button" class="fund-toggle" :aria-expanded="openFunds.has(f.status + ':' + f.fundKey)" :data-testid="`retired-toggle-${f.status}-${f.fundKey}`" @click="toggleFund(f.status + ':' + f.fundKey)">
              <strong>{{ f.fundName }}</strong> · {{ f.status }} · {{ f.statusReason ?? '—' }} · {{ fmt(f.edgeBpPerDay, 1) }} bp/day
            </button>
            <ProspectMeasures v-if="openFunds.has(f.status + ':' + f.fundKey)" :fund="f" />
          </li>
        </ul>
        <h4>Proposed</h4>
        <p v-if="proposed.length === 0" class="dim">none</p>
        <ul v-else class="list">
          <li v-for="f in proposed" :key="f.fundKey" :data-testid="`proposed-${f.fundKey}`">
            <button type="button" class="fund-toggle" :aria-expanded="openFunds.has('proposed:' + f.fundKey)" :data-testid="`proposed-toggle-${f.fundKey}`" @click="toggleFund('proposed:' + f.fundKey)">
              <strong>{{ f.fundName }}</strong> · {{ f.status }} · {{ f.statusReason ?? '—' }} · {{ fmt(f.edgeBpPerDay, 1) }} bp/day
            </button>
            <ProspectMeasures v-if="openFunds.has('proposed:' + f.fundKey)" :fund="f" />
          </li>
        </ul>
      </div>
    </details>
  </div>
</template>

<style scoped>
.ph { color: #c9d1d9; font-size: 0.85rem; min-width: 0; margin-top: 1rem; }
.ph-sec { border-top: 1px solid #21262d; }
.ph-sec > summary, .ph-nested > summary { min-height: 44px; display: flex; align-items: center; cursor: pointer; color: #8b949e; }
.ph-sec > summary:focus-visible, .ph-nested > summary:focus-visible { outline: 2px solid #58a6ff; outline-offset: 2px; }
.ph-body { padding-bottom: 0.6rem; min-width: 0; }
.ph-tools { display: flex; gap: 0.5rem; margin-bottom: 0.5rem; flex-wrap: wrap; }
.ph-tools input { flex: 1; min-width: 10rem; min-height: 44px; box-sizing: border-box; background: #0d1117; color: #c9d1d9; border: 1px solid #30363d; border-radius: 6px; padding: 0 0.6rem; }
.ph-tools button { min-height: 44px; }
.scroll { overflow-x: auto; }
table { border-collapse: collapse; width: 100%; }
th, td { text-align: left; padding: 0.3rem 0.5rem; border-bottom: 1px solid #21262d; vertical-align: top; }
th { color: #8b949e; font-weight: 500; white-space: nowrap; }
.mono { font-family: ui-monospace, monospace; font-size: 0.78rem; overflow-wrap: anywhere; }
td.amber { background: rgba(210, 153, 34, 0.15); color: #f0b72f; }
tr.bad td { background: rgba(210, 153, 34, 0.15); color: #f0b72f; }
pre { white-space: pre-wrap; overflow-wrap: anywhere; margin: 0.3rem 0; }
.err { color: #f85149; }
.dim { color: #8b949e; }
.list { margin: 0.3rem 0; padding-left: 0; list-style: none; }
.fund-toggle { display: block; width: 100%; text-align: left; min-height: 44px; background: transparent; color: inherit; border: 0; border-bottom: 1px solid #21262d; padding: 0.3rem 0.2rem; cursor: pointer; overflow-wrap: anywhere; }
.fund-toggle:focus-visible { outline: 2px solid #58a6ff; outline-offset: 2px; }
.list li, .ph-nested li { overflow-wrap: anywhere; margin-bottom: 0.2rem; }
h4 { margin: 0.8rem 0 0.3rem; color: #8b949e; font-weight: 500; }
</style>
